/**
 * Scheduling Service
 * Handles automated report generation and scheduling
 */

class SchedulingService {
  constructor() {
    this.scheduledReports = new Map();
    this.schedulingHistory = [];
    this.maxHistorySize = 100;
    this.isEnabled = false; // Would be enabled in production
  }

  /**
   * Schedule a report for automatic generation
   */
  async scheduleReport(config) {
    const {
      id,
      reportType,
      parameters,
      schedule, // { frequency, time, daysOfWeek, dayOfMonth }
      format,
      recipients,
      enabled = true,
      name
    } = config;

    // Validate configuration
    const validation = this.validateScheduleConfig(config);
    if (!validation.isValid) {
      throw new Error(`Invalid schedule configuration: ${validation.errors.join(', ')}`);
    }

    const scheduleId = id || this.generateScheduleId();
    const scheduledReport = {
      id: scheduleId,
      name: name || `${reportType} - ${schedule.frequency}`,
      reportType,
      parameters: { ...parameters },
      schedule: { ...schedule },
      format,
      recipients: [...recipients],
      enabled,
      createdAt: new Date().toISOString(),
      lastRun: null,
      nextRun: this.calculateNextRun(schedule),
      runCount: 0,
      successCount: 0,
      failureCount: 0,
      status: 'scheduled'
    };

    this.scheduledReports.set(scheduleId, scheduledReport);

    // Add to history
    this.addToHistory({
      action: 'schedule_created',
      scheduleId,
      timestamp: new Date(),
      details: { name: scheduledReport.name, frequency: schedule.frequency }
    });

    // In a real implementation, this would integrate with a job scheduler
    console.log('📅 Scheduled report created:', scheduledReport);

    return scheduledReport;
  }

  /**
   * Update scheduled report
   */
  async updateScheduledReport(scheduleId, updates) {
    const scheduledReport = this.scheduledReports.get(scheduleId);
    if (!scheduledReport) {
      throw new Error(`Scheduled report not found: ${scheduleId}`);
    }

    const updatedReport = {
      ...scheduledReport,
      ...updates,
      updatedAt: new Date().toISOString()
    };

    // Recalculate next run if schedule changed
    if (updates.schedule) {
      updatedReport.nextRun = this.calculateNextRun(updates.schedule);
    }

    this.scheduledReports.set(scheduleId, updatedReport);

    this.addToHistory({
      action: 'schedule_updated',
      scheduleId,
      timestamp: new Date(),
      details: { changes: Object.keys(updates) }
    });

    return updatedReport;
  }

  /**
   * Delete scheduled report
   */
  async deleteScheduledReport(scheduleId) {
    const scheduledReport = this.scheduledReports.get(scheduleId);
    if (!scheduledReport) {
      throw new Error(`Scheduled report not found: ${scheduleId}`);
    }

    this.scheduledReports.delete(scheduleId);

    this.addToHistory({
      action: 'schedule_deleted',
      scheduleId,
      timestamp: new Date(),
      details: { name: scheduledReport.name }
    });

    return true;
  }

  /**
   * Get all scheduled reports
   */
  getScheduledReports() {
    return Array.from(this.scheduledReports.values());
  }

  /**
   * Get scheduled report by ID
   */
  getScheduledReport(scheduleId) {
    return this.scheduledReports.get(scheduleId) || null;
  }

  /**
   * Enable/disable scheduled report
   */
  async toggleScheduledReport(scheduleId, enabled) {
    const scheduledReport = this.scheduledReports.get(scheduleId);
    if (!scheduledReport) {
      throw new Error(`Scheduled report not found: ${scheduleId}`);
    }

    scheduledReport.enabled = enabled;
    scheduledReport.status = enabled ? 'scheduled' : 'disabled';
    
    // Update next run if enabling
    if (enabled) {
      scheduledReport.nextRun = this.calculateNextRun(scheduledReport.schedule);
    }

    this.addToHistory({
      action: enabled ? 'schedule_enabled' : 'schedule_disabled',
      scheduleId,
      timestamp: new Date(),
      details: { name: scheduledReport.name }
    });

    return scheduledReport;
  }

  /**
   * Manually trigger scheduled report
   */
  async triggerScheduledReport(scheduleId) {
    const scheduledReport = this.scheduledReports.get(scheduleId);
    if (!scheduledReport) {
      throw new Error(`Scheduled report not found: ${scheduleId}`);
    }

    try {
      scheduledReport.status = 'running';
      scheduledReport.lastRun = new Date().toISOString();
      scheduledReport.runCount++;

      // In a real implementation, this would trigger the actual report generation
      const result = await this.simulateReportGeneration(scheduledReport);

      scheduledReport.status = 'completed';
      scheduledReport.successCount++;

      this.addToHistory({
        action: 'report_generated',
        scheduleId,
        timestamp: new Date(),
        details: { 
          name: scheduledReport.name,
          manual: true,
          success: true
        }
      });

      return { success: true, result };
    } catch (error) {
      scheduledReport.status = 'failed';
      scheduledReport.failureCount++;

      this.addToHistory({
        action: 'report_failed',
        scheduleId,
        timestamp: new Date(),
        details: { 
          name: scheduledReport.name,
          manual: true,
          error: error.message
        }
      });

      throw error;
    } finally {
      // Reset status and calculate next run
      setTimeout(() => {
        scheduledReport.status = scheduledReport.enabled ? 'scheduled' : 'disabled';
        if (scheduledReport.enabled) {
          scheduledReport.nextRun = this.calculateNextRun(scheduledReport.schedule);
        }
      }, 1000);
    }
  }

  /**
   * Get reports due for generation
   */
  getDueReports() {
    const now = new Date();
    return this.getScheduledReports().filter(report => 
      report.enabled && 
      report.nextRun && 
      new Date(report.nextRun) <= now
    );
  }

  /**
   * Get scheduling statistics
   */
  getSchedulingStats() {
    const reports = this.getScheduledReports();
    const enabled = reports.filter(r => r.enabled);
    const disabled = reports.filter(r => !r.enabled);
    const totalRuns = reports.reduce((sum, r) => sum + r.runCount, 0);
    const totalSuccess = reports.reduce((sum, r) => sum + r.successCount, 0);
    const totalFailures = reports.reduce((sum, r) => sum + r.failureCount, 0);

    return {
      totalScheduled: reports.length,
      enabled: enabled.length,
      disabled: disabled.length,
      totalRuns,
      totalSuccess,
      totalFailures,
      successRate: totalRuns > 0 ? (totalSuccess / totalRuns) * 100 : 0,
      frequencyDistribution: this.getFrequencyDistribution(reports)
    };
  }

  /**
   * Validate schedule configuration
   */
  validateScheduleConfig(config) {
    const errors = [];

    // Required fields
    if (!config.reportType) errors.push('Report type is required');
    if (!config.schedule) errors.push('Schedule configuration is required');
    if (!config.recipients || !config.recipients.length) {
      errors.push('At least one recipient is required');
    }

    // Schedule validation
    if (config.schedule) {
      const { frequency, time } = config.schedule;
      
      if (!['daily', 'weekly', 'monthly', 'quarterly'].includes(frequency)) {
        errors.push('Invalid frequency. Must be daily, weekly, monthly, or quarterly');
      }

      if (!time || !this.isValidTime(time)) {
        errors.push('Valid time is required (HH:MM format)');
      }

      if (frequency === 'weekly' && !config.schedule.daysOfWeek) {
        errors.push('Days of week required for weekly schedule');
      }

      if (frequency === 'monthly' && !config.schedule.dayOfMonth) {
        errors.push('Day of month required for monthly schedule');
      }
    }

    // Recipients validation
    if (config.recipients) {
      const invalidEmails = config.recipients.filter(email => !this.isValidEmail(email));
      if (invalidEmails.length > 0) {
        errors.push(`Invalid email addresses: ${invalidEmails.join(', ')}`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Calculate next run time based on schedule
   */
  calculateNextRun(schedule) {
    const { frequency, time, daysOfWeek, dayOfMonth } = schedule;
    const now = new Date();
    const [hours, minutes] = time.split(':').map(Number);

    let nextRun = new Date(now);
    nextRun.setHours(hours, minutes, 0, 0);

    switch (frequency) {
      case 'daily':
        if (nextRun <= now) {
          nextRun.setDate(nextRun.getDate() + 1);
        }
        break;

      case 'weekly':
        // Find next occurrence of specified days
        const targetDays = daysOfWeek || [1]; // Default to Monday
        let foundNext = false;
        for (let i = 0; i < 7 && !foundNext; i++) {
          const testDate = new Date(nextRun);
          testDate.setDate(testDate.getDate() + i);
          
          if (targetDays.includes(testDate.getDay()) && testDate > now) {
            nextRun = testDate;
            foundNext = true;
          }
        }
        
        if (!foundNext) {
          // Next week
          nextRun.setDate(nextRun.getDate() + 7);
          while (!targetDays.includes(nextRun.getDay())) {
            nextRun.setDate(nextRun.getDate() + 1);
          }
        }
        break;

      case 'monthly':
        const targetDay = dayOfMonth || 1;
        nextRun.setDate(targetDay);
        
        if (nextRun <= now) {
          nextRun.setMonth(nextRun.getMonth() + 1);
          nextRun.setDate(targetDay);
        }
        break;

      case 'quarterly':
        // First day of next quarter
        const currentQuarter = Math.floor(now.getMonth() / 3);
        const nextQuarter = (currentQuarter + 1) % 4;
        nextRun.setMonth(nextQuarter * 3, 1);

        if (nextRun <= now) {
          nextRun.setFullYear(nextRun.getFullYear() + 1);
          nextRun.setMonth(0, 1); // January 1st
        }
        break;
      default:
        // Default to daily if frequency is not recognized
        console.warn('Unknown frequency, defaulting to daily:', frequency);
        if (nextRun <= now) {
          nextRun.setDate(nextRun.getDate() + 1);
        }
        break;
    }

    return nextRun.toISOString();
  }

  /**
   * Simulate report generation (placeholder)
   */
  async simulateReportGeneration(scheduledReport) {
    // Simulate async operation
    await new Promise(resolve => setTimeout(resolve, 1000));

    return {
      reportType: scheduledReport.reportType,
      generatedAt: new Date().toISOString(),
      format: scheduledReport.format,
      recipients: scheduledReport.recipients,
      success: true
    };
  }

  /**
   * Get frequency distribution
   */
  getFrequencyDistribution(reports) {
    const distribution = {};
    reports.forEach(report => {
      const freq = report.schedule.frequency;
      distribution[freq] = (distribution[freq] || 0) + 1;
    });
    return distribution;
  }

  /**
   * Utility methods
   */
  generateScheduleId() {
    return `schedule_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
  }

  isValidTime(time) {
    return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  addToHistory(entry) {
    this.schedulingHistory.unshift(entry);
    if (this.schedulingHistory.length > this.maxHistorySize) {
      this.schedulingHistory = this.schedulingHistory.slice(0, this.maxHistorySize);
    }
  }

  getSchedulingHistory(limit = 20) {
    return this.schedulingHistory.slice(0, limit);
  }

  clearHistory() {
    this.schedulingHistory = [];
  }

  /**
   * Export/Import scheduled reports
   */
  exportScheduledReports() {
    return {
      version: '1.0.0',
      exportedAt: new Date().toISOString(),
      scheduledReports: Array.from(this.scheduledReports.values())
    };
  }

  async importScheduledReports(exportData) {
    if (!exportData || !exportData.scheduledReports) {
      throw new Error('Invalid export data');
    }

    const imported = [];
    const errors = [];

    for (const reportData of exportData.scheduledReports) {
      try {
        const validation = this.validateScheduleConfig(reportData);
        if (validation.isValid) {
          await this.scheduleReport(reportData);
          imported.push(reportData.id);
        } else {
          errors.push({
            id: reportData.id,
            errors: validation.errors
          });
        }
      } catch (error) {
        errors.push({
          id: reportData.id,
          errors: [error.message]
        });
      }
    }

    return {
      imported: imported.length,
      errors: errors.length,
      details: { imported, errors }
    };
  }
}

const schedulingService = new SchedulingService();
export default schedulingService;